/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Loader } from 'modules/common/components';
import { selectLoader } from 'modules/scenario/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { Card, CardContent, Grid, Typography, Button } from '@mui/material';
import { withTheme } from '@emotion/react';
import { scenarioActions } from 'modules/scenario/slice';
import { isEmpty } from 'modules/common/helpers/object';
import ORDER_BY from 'modules/common/constants/order-by';
import ScenarioTable from '../scenario-table';
import AddScenarioForm from '../add-scenario';
import UpdateScenarioForm from '../update-scenario';
import FilterModal from '../add-scenario/filter-model';
import { ENVIRONMENT } from '../../../../config';
/**
 * Scenario view to add, edit and delete Power BI Scenarios
 * @returns
 */
const ScenarioView = ({ theme }) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const loading = useSelector(selectLoader);
  //
  const [selectedScenario, setSelectedScenario] = useState({});
  const [selectedVisual, setSelectedVisual] = useState();
  const [filters, setFilters] = useState();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [show, isShow] = useState(false);
  // Fetch scenarios, visual filters, hotels, and breakdown data whenever organizationId changes
  useEffect(() => {
    const query = `limit=${PAGINATION.SCENARIOS_PER_PAGE_COUNT}`;
    dispatch(
      scenarioActions.getScenarios({
        organizationId,
        query,
      })
    );
    //
    const visualFiltersQuery = `limit=${PAGINATION.VISUAL_FILTERS_PER_PAGE_COUNT}&sortBy=name:${ORDER_BY.ASCENDING}`;
    dispatch(
      scenarioActions.getVisualFilters({
        organizationId,
        query: visualFiltersQuery,
      })
    );
    //
    const forceQuery = `limit=${PAGINATION.HOTELS_PER_PAGE_COUNT}&sortBy=name:${ORDER_BY.ASCENDING}&forceFetch=true`;
    dispatch(
      scenarioActions.getAllHotelList({
        organizationId,
        query: forceQuery,
      })
    );
    //
    const breakdownQuery = `limit=${PAGINATION.BREAKDOWN_DATA}`;
    dispatch(scenarioActions.getBreakdown({ organizationId, query: breakdownQuery }));
    //
    const visualQuery = `limit=${PAGINATION.VISUALS_PER_PAGE_COUNT}&sortBy=name:${ORDER_BY.ASCENDING}`;
    dispatch(
      scenarioActions.getVisuals({
        organizationId,
        query: visualQuery,
      })
    );
  }, [organizationId]);
  //
  return (
    ENVIRONMENT.ENABLE_SCENARIO_TAB === 'true' && (
      <Loader loading={loading}>
        <FilterModal
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          selectedVisual={selectedVisual}
          filters={filters}
          setFilters={setFilters}
        />
        <Card>
          <CardContent sx={{ backgroundColor: theme.palette.background.default }}>
            <Grid container direction="row" justifyContent="space-between" pb={4}>
              <Typography variant="h3">Configure Scenarios</Typography>
              <Button
                disabled={show}
                variant="contained"
                color="primary"
                onClick={() => setOpen(true)}
              >
                Add Scenario
              </Button>
            </Grid>
            <Grid container direction="row" spacing={2} justifyContent="space-between">
              <Grid item sx={{ flexGrow: 1, flexBasis: 1 }}>
                <ScenarioTable isShow={isShow} setSelectedScenario={setSelectedScenario} />
              </Grid>
              {!isEmpty(selectedScenario) && (
                <UpdateScenarioForm show={show} isShow={isShow} scenario={selectedScenario} />
              )}
            </Grid>
          </CardContent>
          <AddScenarioForm
            open={open}
            onClose={() => setOpen(false)}
            setSelectedVisual={setSelectedVisual}
            setFilters={setFilters}
            setFilterModalOpen={setFilterModalOpen}
            visualFilters={filters}
          />
        </Card>
      </Loader>
    )
  );
};
//
export default withTheme(ScenarioView);
